import { AutoCompleteInput, Button, Form, IAutoCompleteOption, Icon, TextInput } from "armstrong-react";
import { Col, Grid, Row } from "armstrong-react-fix";
import React from "react";
import _ from "underscore";

import { SingleDatePicker } from "react-dates";
import * as DateUtil from "@travellocal/utils/dateHelpers";
import { ToggleSwitch } from "./../../../core/atoms/toggleSwitch";
import { api } from "./../../../shared/api/system/apiClient";
import { ValidationBar } from "./../../validationBar";
import { DatePicker } from "./../datePicker";
import { TimePicker } from "./../timePicker";

import "./travellerDetailsForm.scss";

enum FlightType {
  outbound,
  inbound,
}

interface ITravellerDetailsFormProps extends React.Props<TravellerDetailsForm> {
  ltoName: string;
  data: ITravelerDetails | ILeadTravelerDetails;
  save: (data: ITravelerDetails | ILeadTravelerDetails) => void;
  isLeadTraveler?: boolean;
  canSubmit?: boolean;
  errors?: IValidationError[];
}

interface State {
  monthsToShow: number;
  arrivalDateFocus: boolean;
  departureDateFocus: boolean;
}

export class TravellerDetailsForm extends React.Component<ITravellerDetailsFormProps, State> {
  public constructor(props) {
    super(props);
    this.state = {
      monthsToShow: 2,
      arrivalDateFocus: false,
      departureDateFocus: false,
    };
  }

  public updateDate(propName: string, date: IDate) {
    this.props.data[propName] = date;
    this.forceUpdate();
  }

  public updateFlightDate(outbound: boolean, propName: string, date: IDate) {
    const p = outbound ? this.props.data.outboundFlight : this.props.data.returnFlight;
    p[propName] = date;
    this.forceUpdate();
  }

  public updateFlightTime(outbound: boolean, propName: string, time: ITimeOfDay) {
    const p = outbound ? this.props.data.outboundFlight : this.props.data.returnFlight;
    p[propName] = time;
    this.forceUpdate();
  }

  public updateTitle(title: Title) {
    this.props.data.title = title;
    if (title !== "other") {
      this.props.data.otherTitle = null;
    }
    this.forceUpdate();
  }
  public updatePassportCountry(country: IIsoCountry) {
    this.props.data.passportIssuingCountry = country;
    this.forceUpdate();
  }

  public updateCountryOfResidence(country: IIsoCountry) {
    this.props.data.countryOfResidence = country;
    this.forceUpdate();
  }

  public getSelectedCountry(prop: string): IAutoCompleteOption {
    if (this.props.data[prop]) {
      const country = this.props.data[prop] as IIsoCountry;
      return { id: country.id, name: country.name, data: country };
    }
    return null;
  }

  public toggleFlight(on: boolean, type: FlightType) {

    const newFlight = { carrier: "", number: "", departureTime: null, arrivalTime: null, departureDate: null, arrivalDate: null };

    if (type === FlightType.inbound) {
      on ? this.props.data.returnFlight = newFlight : this.props.data.returnFlight = null;
    }
    if (type === FlightType.outbound) {
      on ? this.props.data.outboundFlight = newFlight : this.props.data.outboundFlight = null;
    }

    this.forceUpdate();
  }

  private handleResize = () => {
    if (window.innerWidth <= 630) {
      this.setState({monthsToShow: 1});
    } else {
      this.setState({monthsToShow: 2});
    }
  }

  public componentDidMount() {
    this.handleResize();
    requestAnimationFrame(() => {
      window.addEventListener("resize", this.handleResize);
    });
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  public render() {

    const titles = [
      { value: "mr", name: "Mr" },
      { value: "mrs", name: "Mrs" },
      { value: "master", name: "Master" },
      { value: "miss", name: "Miss" },
      { value: "ms", name: "Ms" },
      { value: "dr", name: "Dr" },
      { value: "other", name: "Other" }];

    return <Form className="traveller-details-form" dataBinder={Form.jsonDataBinder(this.props.data)} onDataChanged={() => this.forceUpdate()}>

      {this.props.errors.length !== 0 && <ValidationBar errors={this.props.errors} />}

      <p>These details must be accurate and where applicable, as per {this.props.isLeadTraveler ? "your" : "the traveller's"} passport.
       Please check the details carefully. Neither TravelLocal nor {this.props.ltoName} can be held liable for inaccuracies.</p>
      <h3>Personal details</h3>
      <hr className="m-bottom-small" />

      <Grid className="form-grid rs-xlarge-spaced rs-medium-1col">
        <Row>
          <Col className="rs-medium-unfix" width="auto">
            <label>Title</label>
            <select onChange={(e) => this.updateTitle(e.target.value as Title)} value={this.props.data.title}>
              <option value=" "></option>
              {titles.map(t => <option key={t.value} value={t.value}>{t.name}</option>)}
            </select>
          </Col>
          {this.props.data.title === "other" && <Col>
            <label>Other title</label>
            <TextInput {...Form.Bind.text("otherTitle") } placeholder="Title..." />
          </Col>
          }
          <Col>
            <label>First name</label>
            <TextInput {...Form.Bind.text("firstName") } placeholder="First name..." />
          </Col>
          <Col>
            <label>Last name</label>
            <TextInput {...Form.Bind.text("lastName") } placeholder="Last name..." />
          </Col>
        </Row>

        <Row>
          <Col className="sub-form">
            <label>Date of birth</label>
            <DatePicker date={this.props.data.dateOfBirth} onChange={(d) => this.updateDate("dateOfBirth", d)} />
          </Col>
          <Col>
            <label>Email address</label>
            <TextInput readonly={this.props.isLeadTraveler} {...Form.Bind.textEmail("emailAddress") } placeholder="Email address..." />
          </Col>
        </Row>

        {this.props.isLeadTraveler &&
          <Row>
            <Col>
              <label>Home phone number (include international dialling code)</label>
              <TextInput {...Form.Bind.text("homePhone") } placeholder="eg. +44 123000000" />
            </Col>
            <Col>
              <label>Phone number whilst away (include international dialling code)</label>
              <TextInput {...Form.Bind.text("travelPhone") } placeholder="eg. +44 123000000" />
            </Col>
          </Row>
        }

        {this.props.isLeadTraveler &&
          <Row>
            <Col>
              <label>Address</label>
              <TextInput {...Form.Bind.text("address") } multiLine={true} placeholder="123 Example Road, \r\nTest town\r\n\BA1 1AA\r\nUnited Kingdom" />
            </Col>
          </Row>
        }

        {this.props.isLeadTraveler &&
          <Row>
            <Col>
              <label>Country of residence</label>
              <AutoCompleteInput placeholder="Start typing to filter..."
                value={this.getSelectedCountry("countryOfResidence")}
                className="m-bottom-small m-top-xxsmall"
                remoteQueryOnOpen={true}
                remoteThrottle={100}
                onSelected={(item: IAutoCompleteOption) => this.updateCountryOfResidence(item.data as IIsoCountry)}
                remoteQuery={(input) => api.getIsoCountries(input, null).then((cs) => _.map(cs.json.payload, l => ({ id: l.id, name: l.name, data: l })))} />
            </Col>
          </Row>
        }

        {this.props.isLeadTraveler &&
          <Row>
            <Col>
              <label>Room requirements</label>
              <TextInput {...Form.Bind.text("roomRequirements") } multiLine={true} placeholder="Any special requirements you have for your room(s)..." />
            </Col>
          </Row>
        }

        <Row>
          <Col>
            <label>Dietary requirements</label>
            <TextInput {...Form.Bind.text("dietaryRequirements") } placeholder="Dietary requirements..." multiLine={true} />
          </Col>
        </Row>
      </Grid>

      {this.props.isLeadTraveler &&
        <div>
          <h3 className="m-top-small">Emergency contact details</h3>
          <p>By completing this information, you confirm that you have the consent of the person specified for their details
           to be held for the purposes of contacting them in the event of an emergency.</p>
          <hr className="m-bottom-small" />
          <Grid className="form-grid rs-xlarge-2col rs-xlarge-spaced rs-medium-1col">
            <Row>
              <Col>
                <label>Emergency contact name</label>
                <TextInput {...Form.Bind.text("emergencyContactName") } placeholder="e.g. John Smith" />
              </Col>
              <Col>
                <label>Emergency contact phone number (include international dialling code)</label>
                <TextInput {...Form.Bind.text("emergencyContactNumber") } placeholder="eg. +44 123000000" />
              </Col>
            </Row>
          </Grid>

          <h3 className="m-top-small">Travel insurance information</h3>
          <p>Supplying this information will allow {this.props.ltoName} to contact your insurer in case of emergency.</p>
          <hr className="m-bottom-small" />

          <Grid className="form-grid rs-xlarge-2col rs-xlarge-spaced rs-medium-1col">
            <Row>
              <Col>
                <label>Travel insurance provider</label>
                <TextInput {...Form.Bind.text("travelInsuranceProvider") } placeholder="Name of travel insurance company" />
              </Col>
              <Col>
                <label>Insurance policy number</label>
                <TextInput {...Form.Bind.text("travelInsurancePolicyNumber") } placeholder="12345" />
              </Col>
              <Col>
                <label>Insurance provider's emergency phone number (include international dialling code)</label>
                <TextInput {...Form.Bind.text("travelInsuranceEmergencyNumber") } placeholder="eg. +44 123000000" />
              </Col>
            </Row>
          </Grid>
        </div>
      }

      <h3 className="m-top-small">Passport details</h3>
      <hr className="m-bottom-small" />

      <Grid className="form-grid rs-xlarge-2col rs-xlarge-spaced rs-medium-1col">
        <Row>
          <Col>
            <label>Passport no.</label>
            <TextInput {...Form.Bind.text("passportNumber") } placeholder="Passport number..." />
          </Col>
          <Col className="sub-form">
            <label>Passport expiry date</label>
            <DatePicker date={this.props.data.passportExpiryDate} yearsFromNow={20} futureDates={true} onChange={(d) => this.updateDate("passportExpiryDate", d)} />
          </Col>
          <Col>
            <label>Passport issuing country</label>
            <AutoCompleteInput placeholder="Start typing to filter..."
              value={this.getSelectedCountry("passportIssuingCountry")}
              className="m-bottom-small m-top-xxsmall"
              remoteQueryOnOpen={true}
              remoteThrottle={100}
              onSelected={(item: IAutoCompleteOption) => this.updatePassportCountry(item.data as IIsoCountry)}
              remoteQuery={(input) => api.getIsoCountries(input, null).then((cs) => _.map(cs.json.payload, l => ({ id: l.id, name: l.name, data: l })))} />
          </Col>
        </Row>
      </Grid>

      <h3>Outbound flight details</h3>
      <hr className="m-bottom-small" />

      {!this.props.isLeadTraveler &&
        <Grid className="m-bottom-small">
          <Row>
            <Col width="auto" className="p-right-small" verticalAlignment="center">Different flight to lead passenger?</Col>
            <Col verticalAlignment="center" horizontalAlignment="center">
              <ToggleSwitch switchedOn={!!this.props.data.outboundFlight} onLabel="Yes" offLabel="No" onToggle={(isOn) => this.toggleFlight(isOn, FlightType.outbound)} />
            </Col>
          </Row>
        </Grid>
      }

      {this.props.data.outboundFlight &&
        <Grid className="form-grid rs-xlarge-spaced rs-medium-1col">
          <Row>
            <Col>
              <label>Carrier</label>
              <TextInput {...Form.Bind.text("outboundFlight.carrier") } placeholder="Airline's name..." />
            </Col>
            <Col>
              <label>Flight number</label>
              <TextInput {...Form.Bind.text("outboundFlight.number") } placeholder="Flight number..." />
            </Col>
          </Row>

          <Row>
            <Col className="flight-date">
              <label>Date of arrival</label>
              <SingleDatePicker
                date={this.props.data.outboundFlight.arrivalDate ? DateUtil.toMoment(this.props.data.outboundFlight.arrivalDate) : null}
                onDateChange={newDate => newDate && this.updateFlightDate(true, "arrivalDate", DateUtil.momentToDate(newDate))}
                focused={this.state.arrivalDateFocus}
                onFocusChange={({ focused }) => this.setState({ arrivalDateFocus: focused })}
                id="flight_arrival_date"
                noBorder={true}
                numberOfMonths={this.state.monthsToShow}
                placeholder={"Arrival date"}
                readOnly
              />
            </Col>
            <Col className="sub-form">
              <label>Time of arrival</label>
              <TimePicker onChange={(t) => this.updateFlightTime(true, "arrivalTime", t)} time={this.props.data.outboundFlight.arrivalTime} />
            </Col>
          </Row>
        </Grid>
      }

      <h3>Return flight details</h3>
      <hr className="m-bottom-small" />

      {!this.props.isLeadTraveler &&
        <Grid className="m-bottom-small">
          <Row>
            <Col width="auto" className="p-right-small" verticalAlignment="center">Different flight to lead passenger?</Col>
            <Col verticalAlignment="center" horizontalAlignment="center">
              <ToggleSwitch switchedOn={!!this.props.data.returnFlight} onLabel="Yes" offLabel="No" onToggle={(isOn) => this.toggleFlight(isOn, FlightType.inbound)} />
            </Col>
          </Row>
        </Grid>
      }

      {this.props.data.returnFlight &&
        <Grid className="form-grid rs-xlarge-spaced rs-medium-1col">
          <Row className="rs-medium-1col">
            <Col>
              <label>Carrier</label>
              <TextInput {...Form.Bind.text("returnFlight.carrier") } placeholder="Airline's name..." />
            </Col>
            <Col>
              <label>Flight number</label>
              <TextInput {...Form.Bind.text("returnFlight.number") } placeholder="Flight number..." />
            </Col>
          </Row>

          <Row>
            <Col className="flight-date">
              <label>Date of departure</label>
              <SingleDatePicker
                date={this.props.data.returnFlight.departureDate ? DateUtil.toMoment(this.props.data.returnFlight.departureDate) : null}
                onDateChange={newDate => newDate && this.updateFlightDate(false, "departureDate", DateUtil.momentToDate(newDate))}
                focused={this.state.departureDateFocus}
                onFocusChange={({ focused }) => this.setState({ departureDateFocus: focused })}
                id="flight_departure_date"
                noBorder={true}
                numberOfMonths={this.state.monthsToShow}
                placeholder={"Departure date"}
                readOnly
              />
            </Col>
            <Col className="sub-form">
              <label>Time of departure</label>
              <TimePicker onChange={(t) => this.updateFlightTime(false, "departureTime", t)} time={this.props.data.returnFlight.departureTime} />
            </Col>
          </Row>
        </Grid>
      }

      {this.props.isLeadTraveler &&
        <div>
          <h3 className="m-top-small">How did you hear about TravelLocal?</h3>
          <hr className="m-bottom-small" />
          <Grid className="form-grid rs-xlarge-spaced rs-medium-1col">
            <Row>
              <Col>
                <TextInput {...Form.Bind.text("whereYouHeardOfUs") } placeholder="Please let us know where you heard about us..." />
              </Col>
            </Row>
          </Grid>
        </div>
      }

      <Grid>
        <Row>
          <Col className="p-bottom-medium">
            <Button disabled={!this.props.canSubmit} type="button" className="primary-button heading--4 text--light" onClick={() => this.props.save(this.props.data)}>
              Save
            </Button>
          </Col>
        </Row>
      </Grid>
    </Form>;
  }
}
